/**
 * Where each piece of printing goes, and the print helper that sends it there.
 *
 * Two ways to print, chosen per shop (Settings -> Printing):
 *   the print window - the browser's own, works everywhere, asks every time;
 *   the print helper - a small program on the till PC (tools/print-helper) that
 *                     prints to a named printer with no window at all, and
 *                     opens the cash drawer.
 * A web page cannot pick a printer or print silently by itself; that is the
 * whole reason the helper exists.
 *
 * If the helper is not answering, a print falls back to the print window and
 * says why - a sale must never end with no receipt because a program on the PC
 * was closed.
 */
import type { StoreSettings } from '@pos/shared';
import { PAPER_DOTS, kickJob, lastInkRow, rasterJob, toBase64, toMono, type DrawerPin } from './escpos.js';
import { renderToCanvas } from './raster.js';

export const DEFAULT_HELPER_URL = 'http://127.0.0.1:8722';
const HELPER_KEY = 'pos.printHelperUrl';

/** The helper runs on THIS computer, so where to find it is this device's business. */
export function helperUrl(): string {
  try {
    return localStorage.getItem(HELPER_KEY) || DEFAULT_HELPER_URL;
  } catch {
    return DEFAULT_HELPER_URL;
  }
}

export function setHelperUrl(url: string): void {
  try {
    if (!url || url === DEFAULT_HELPER_URL) localStorage.removeItem(HELPER_KEY);
    else localStorage.setItem(HELPER_KEY, url);
  } catch {
    /* private window */
  }
}

export interface PrintRoute {
  viaHelper: boolean;
  receiptPrinter: string;
  paper: '80' | '58';
  labelPrinter: string;
  drawer: { enabled: boolean; pin: DrawerPin; onCash: boolean };
}

let route: PrintRoute = {
  viaHelper: false,
  receiptPrinter: '',
  paper: '80',
  labelPrinter: '',
  drawer: { enabled: false, pin: '2', onCash: true },
};

/** Called whenever the shop's settings arrive or change. */
export function setPrintRoute(s: StoreSettings): void {
  route = {
    viaHelper: s.printVia === 'helper',
    receiptPrinter: s.receiptPrinter ?? '',
    paper: s.receiptPaper === '58' ? '58' : '80',
    labelPrinter: s.labelPrinter ?? '',
    drawer: { enabled: !!s.drawerEnabled, pin: (s.drawerPin as DrawerPin) || '2', onCash: s.drawerOnCash !== false },
  };
}

export function printRoute(): PrintRoute {
  return route;
}

/** Something went wrong on the way to the printer; the app shows it as a message. */
export const PRINT_PROBLEM = 'pos:print-problem';
export function notifyPrintProblem(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  window.dispatchEvent(new CustomEvent(PRINT_PROBLEM, { detail: message }));
}

// ---------------------------------------------------------------------------
// Talking to the helper
// ---------------------------------------------------------------------------

export interface HelperHealth {
  ok: boolean;
  version?: string;
  canPrint?: boolean;
  error?: string;
}

export interface HelperPrinter {
  name: string;
  port: string | null;
  default?: boolean;
}

async function call<T = Record<string, unknown>>(path: string, timeoutMs: number, body?: unknown, base?: string) {
  const url = (base || helperUrl()).replace(/\/$/, '');
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${url}${path}`, {
      method: body !== undefined ? 'POST' : 'GET',
      signal: ctrl.signal,
      ...(body !== undefined ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {}),
    });
    const data = (await res.json().catch(() => ({}))) as T & { ok?: boolean; error?: string };
    if (!res.ok || data.ok === false) return { ...data, ok: false, error: data.error ?? `HTTP ${res.status}` };
    return { ...data, ok: true };
  } catch (e) {
    // A refused connection, a timeout and a blocked private-network request all
    // mean one thing to the person at the till: the helper is not answering.
    return { ok: false, error: e instanceof Error && e.name === 'AbortError' ? 'timeout' : 'not_running' } as T & {
      ok: boolean;
      error?: string;
    };
  } finally {
    clearTimeout(timer);
  }
}

export class HelperError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

export const helperHealth = (base?: string) => call<HelperHealth>('/health', 2500, undefined, base);

export async function helperPrinters(): Promise<{ printers: HelperPrinter[]; error?: string }> {
  const r = await call<{ printers?: HelperPrinter[] }>('/printers', 12_000);
  // Windows can refuse to list printers ("Generic failure"); the name can then
  // be typed instead, so the list is a convenience and never the only way.
  return r.ok ? { printers: r.printers ?? [] } : { printers: [], error: r.error };
}

async function must(p: Promise<{ ok: boolean; error?: string }>): Promise<void> {
  const r = await p;
  if (!r.ok) throw new HelperError(r.error ?? 'failed');
}

// ---------------------------------------------------------------------------
// What gets printed
// ---------------------------------------------------------------------------

/** Receipt-roll printing: the page drawn as dots and sent as printer commands. */
export async function printRollViaHelper(
  bodyHtml: string,
  opts: { kick?: boolean; dir?: 'rtl' | 'ltr'; lang?: string; printer?: string; paper?: '80' | '58' } = {},
): Promise<void> {
  const paper = opts.paper ?? route.paper;
  const widthMm = paper === '58' ? 48 : 72;
  const dots = PAPER_DOTS[paper];
  // The roll's printable width, edge to edge: the receipt's own margins were
  // for a sheet with unprintable borders, which a picture does not have.
  const fit = `<style>body{width:${widthMm}mm !important;padding:1mm 0.5mm 2mm !important;margin:0 !important}</style>`;
  const canvas = await renderToCanvas(bodyHtml + fit, { widthMm, dots, dir: opts.dir, lang: opts.lang });
  const { data, width, height } = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height);
  const mono = toMono(data, width, height);
  const last = Math.min(height, lastInkRow(mono, width, height) + 24);
  const job = rasterJob(mono.subarray(0, width * Math.max(1, last)), width, Math.max(1, last), {
    kick: opts.kick && route.drawer.enabled ? route.drawer.pin : null,
  });
  await must(call('/print', 20_000, { printer: opts.printer ?? route.receiptPrinter, base64: toBase64(job) }));
}

/** Labels: one picture per different label, printed through the label printer's driver. */
export async function printLabelsViaHelper(
  pages: { html: string; count: number }[],
  size: { widthMm: number; heightMm: number },
  printer = route.labelPrinter,
): Promise<void> {
  const images: string[] = [];
  const counts: number[] = [];
  for (const p of pages) {
    if (p.count <= 0) continue;
    const dots = Math.round(size.widthMm * 8);
    const canvas = await renderToCanvas(p.html, { widthMm: size.widthMm, heightMm: size.heightMm, dots });
    const ctx = canvas.getContext('2d')!;
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    // Pure black and white: a label printer's driver would otherwise grey the barcode.
    const mono = toMono(img.data, canvas.width, canvas.height);
    for (let i = 0; i < mono.length; i++) {
      const v = mono[i] ? 0 : 255;
      img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    images.push(canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, ''));
    counts.push(p.count);
  }
  if (!images.length) return;
  await must(call('/print-image', 60_000, { printer, widthMm: size.widthMm, heightMm: size.heightMm, images, counts }));
}

/** Open the drawer through the helper. Only for a shop that prints through it. */
export async function openDrawerViaHelper(): Promise<void> {
  await must(call('/print', 8000, { printer: route.receiptPrinter, base64: toBase64(kickJob(route.drawer.pin)) }));
}

/** In words a cashier can act on. */
export function helperProblem(code: string, t: (k: 'print.notRunning' | 'print.failed') => string): string {
  return code === 'not_running' || code === 'timeout' ? t('print.notRunning') : code || t('print.failed');
}
